import { useSession } from "@/session/useSession";
import { useEffect, useMemo, useState } from "react";
import { proposalDeny, proposalOpen, requisitionChangeOpenings, todayQueue, type ProposalOpenOutput } from "@/api/tools";
import { useRead, useWrite } from "@/api/useTool";
import { useViewState } from "@/shell/viewState";
import { Banner, Dialog, Empty, KV, ObjectReadGuard, Pill, ReasonDialog, Receipt, Skeleton, fmtDate } from "@/ui";

// V1-INTERACTIONS §4.24 — Drafts waiting for me: `#proposals` and `#proposals/<pr>`. A proposal is something the
// application prepared for ONE seat to decide. Until this screen they were reachable only from a notification (Codex #9
// M10): the list is built from this person's own Today cards, every one of which carries the proposal's id, so it is
// what they were actually asked for and never everybody's drafts — there is no proposal list tool in V1.
//
// There is no `proposal.approve`. Approving IS calling the proposal's own tool with `proposal_id`: this screen carries
// the openings change (`requisition.change_openings`) itself, and a draft whose tool it does not carry is read and
// turned down here and approved where that tool lives.

const KIND_LABEL: Record<string, string> = {
  openings_change: "Openings change",
  kickoff_email: "Kickoff email",
  correction_note: "Correction note",
  phase_flag: "Phase flag",
  scorecard_reminder: "Scorecard reminder",
  closing_note: "Closing note",
};
const STATUS_TONE: Record<string, "ok" | "warn" | "bad" | "info" | "muted"> = { draft: "warn", approved: "ok", denied: "muted", expired: "muted", superseded: "muted" };
const STATUS_WORDS: Record<string, string> = {
  draft: "waiting for you",
  approved: "approved",
  denied: "turned down",
  expired: "expired — nothing more can be decided on it",
  superseded: "replaced by a later draft",
};
/** The tool an approval runs, and where it lives when this screen does not carry it (§4.24 "Approve"). */
const ELSEWHERE: Record<string, { words: string; route: (rid: string | null) => string }> = {
  kickoff_email: { words: "The kickoff is sent from Packets & Sheets, where its own dialog and rules are.", route: (rid) => (rid ? `#requisitions/${rid}/packets` : "#packets") },
  correction_note: { words: "The correction note is sent from Packets & Sheets.", route: (rid) => (rid ? `#requisitions/${rid}/packets` : "#packets") },
  phase_flag: { words: "A phase flag is raised and acknowledged from Today.", route: () => "#today" },
  scorecard_reminder: { words: "A reminder is sent from the packet it is about.", route: (rid) => (rid ? `#requisitions/${rid}/packets` : "#packets") },
  closing_note: { words: "A closing note is sent from the disposition worklist.", route: (rid) => (rid ? `#requisitions/${rid}/offer` : "#today") },
};

const kindWords = (kind: string) => KIND_LABEL[kind] ?? kind.replace(/_/g, " ");

// ---- #proposals -------------------------------------------------------------------------------------------------------

export function ProposalsHome() {
  const { register } = useViewState();
  const q = useRead(["today"], () => todayQueue({}));
  const ids = useMemo(() => {
    const d = q.data;
    const rows = d ? [...d.needs_you, ...d.overdue, ...d.waiting, ...d.upcoming, ...d.recent] : [];
    const seen = new Map<string, { overdue: boolean; asked_at: string }>();
    for (const r of rows) {
      if (r.ref_type !== "proposal" || !r.ref_id) continue;
      const had = seen.get(r.ref_id);
      seen.set(r.ref_id, { overdue: (had?.overdue ?? false) || r.overdue, asked_at: had?.asked_at ?? r.created_at });
    }
    return Array.from(seen.entries()).map(([id, x]) => ({ id, ...x }));
  }, [q.data]);
  useEffect(() => register({ view: "proposals", ids: {}, summary: `drafts ${ids.length}` }), [register, ids.length]);
  if (q.isLoading) return <Skeleton rows={4} />;
  // a read that FAILED is an error with its correlation id, never an empty list: "nothing waiting" and "we could not
  // ask" must never look the same (Codex #9 M10)
  if (q.isError || !q.data) return <Banner error={q.error} onRetry={() => void q.refetch()} />;
  return (
    <div>
      <div className="hrow">
        <div>
          <h1 className="page">Drafts</h1>
          <p className="lede">What the application has prepared for you to decide. Approving a draft is sending it with the tool it belongs to, so the act, its rules and its receipt are that tool&apos;s.</p>
        </div>
      </div>
      {ids.length === 0 ? (
        <Empty level={2} heading="No drafts to review">No draft is waiting for you.</Empty>
      ) : (
        <ul className="rows">
          {ids.map((x) => (
            <ProposalRow key={x.id} id={x.id} overdue={x.overdue} askedAt={x.asked_at} />
          ))}
        </ul>
      )}
    </div>
  );
}

function ProposalRow({ id, overdue, askedAt }: { id: string; overdue: boolean; askedAt: string }) {
  const q = useRead(["proposal", id], () => proposalOpen({ proposal_id: id }), { retry: false });
  const p = q.data;
  return (
    <li className={"rowitem" + (overdue ? " rowitem--overdue" : "")}>
      <span className="rowitem__glyph" aria-hidden="true">
        {overdue ? "▲" : "◆"}
      </span>
      <div className="rowitem__body">
        <div className="rowitem__title">
          {p ? kindWords(p.kind) : "Draft"}
          {p?.requisition_title ? ` — ${p.requisition_title}` : ""}
        </div>
        <div className="rowitem__meta muted">
          {p?.why ? `${p.why} · ` : ""}
          raised {fmtDate(p?.drafted_at ?? askedAt, true)}
          {p ? ` · ${STATUS_WORDS[p.status] ?? p.status}` : ""}
        </div>
      </div>
      {q.isError ? (
        <span className="muted">could not be read</span>
      ) : (
        <a className="btn btn--sm btn--primary" href={`#proposals/${id}`}>
          Open →
        </a>
      )}
    </li>
  );
}

// ---- #proposals/<pr> ---------------------------------------------------------------------------------------------------

export function ProposalPage({ id }: { id: string }) {
  const { me } = useSession();
  const { register } = useViewState();
  const q = useRead(["proposal", id], () => proposalOpen({ proposal_id: id }), { retry: false });
  const inval = [["proposal", id], ["today"], ["requisitions"], ["requisition"]] as const;
  const approve = useWrite((i: { requisition_id: string; proposal_id: string }, k) => requisitionChangeOpenings(i, k), inval);
  const deny = useWrite((i: { proposal_id: string; reason: string }, k) => proposalDeny(i, k), inval);
  const [dlg, setDlg] = useState<null | "approve" | "deny">(null);
  useEffect(() => register({ view: "proposal", ids: { proposal: id }, summary: `draft ${q.data?.kind ?? ""} ${q.data?.status ?? ""}` }), [register, id, q.data?.kind, q.data?.status]);
  if (!q.data || q.error) return <ObjectReadGuard query={q} />;
  const p: ProposalOpenOutput = q.data;
  const openings = p.kind === "openings_change";
  const from = p.artifact["from_count"];
  const to = p.artifact["to_count"];
  // The tool this screen carries; anything else is read and turned down here, and approved where its tool lives (§4.24).
  // A draft's `resulting_tool` is only written when it IS approved, so it cannot say which tool an approval would run:
  // the kind does, and the tool's own seat rule still decides whether this reader may run it.
  const live = p.status === "draft" && !p.expired;
  const carried = openings && !!p.requisition_id;
  const elsewhere = !carried && live ? ELSEWHERE[p.kind] : undefined;
  return (
    <div>
      <div className="hrow">
        <div>
          <h1 className="page">{kindWords(p.kind)}</h1>
          <p className="lede">
            <a href="#proposals">← drafts</a>
            {p.requisition_id ? (
              <>
                {" · "}
                <a href={`#requisitions/${p.requisition_id}`}>{p.requisition_title ?? p.requisition_id}</a>
              </>
            ) : null}
          </p>
        </div>
        <div className="acts">
          <Pill tone={STATUS_TONE[p.status] ?? "muted"}>{STATUS_WORDS[p.status] ?? p.status}</Pill>
          {carried && live && (
            <button className="btn btn--primary" onClick={() => setDlg("approve")}>
              Approve the change
            </button>
          )}
          {live && p.can["deny"] && (
            <button className="btn" onClick={() => setDlg("deny")}>
              Turn it down
            </button>
          )}
        </div>
      </div>
      <Banner error={approve.error ?? deny.error} />
      {approve.isSuccess && <Receipt>Approved — the openings are changed and everyone on the requisition is told.</Receipt>}
      {deny.isSuccess && <Receipt>Turned down — the card stops waiting and the person who drafted it is told.</Receipt>}
      {p.expired && <p className="muted">This draft expired before it was decided. Nothing rewrites it; ask for a fresh one.</p>}
      {elsewhere && (
        <div className="callout callout--warn" role="status">
          {elsewhere.words} <a href={elsewhere.route(p.requisition_id)}>Open it there</a>.
        </div>
      )}
      <section className="card">
        <h2 className="sec">What it says</h2>
        {!openings && <ArtifactBody artifact={p.artifact} />}
        <KV
          items={[
            ["Why it was drafted", p.why ?? "—"],
            ["Raised", `${fmtDate(p.drafted_at, true)}${p.proposed_by ? ` · ${p.proposed_by}${p.proposed_by_role ? ` (${p.proposed_by_role})` : ""}` : ""}`],
            ...(!me?.roles.includes(p.approver_role) ? [["Waiting on", p.approver_role_label] as [string, string]] : []),
            ["Prepared by", p.produced_by === "template" ? "Drafted by the application from a template" : p.produced_by],
            ...(p.expires_at ? ([["Expires", fmtDate(p.expires_at, true)]] as [string, string][]) : []),
            ...(p.decided_at ? ([["Decided", `${fmtDate(p.decided_at, true)}${p.decided_by ? ` · ${p.decided_by}` : ""}`]] as [string, string][]) : []),
            ...(p.decision_reason ? ([["Reason given", p.decision_reason]] as [string, string][]) : []),
          ]}
        />
      </section>
      <section className="card card--content">
        <h2 className="sec">What it would change</h2>
        {openings ? (
          <KV items={[["Openings", `${String(from ?? "—")} → ${String(to ?? "—")}`]]} />
        ) : p.before_after ? (
          <KV items={Object.entries(p.before_after).map(([field, v]) => [field.replace(/_/g, " "), `${beforeAfter(v, "before")} → ${beforeAfter(v, "after")}`] as [string, string])} />
        ) : (
          <p className="muted">Nothing on the record changes until it is sent; the draft is the whole of it.</p>
        )}
      </section>
      <Dialog title="Approve the openings change" open={dlg === "approve"} onClose={() => setDlg(null)}>
        <p>
          The approved openings go from <b>{String(from ?? "—")}</b> to <b>{String(to ?? "—")}</b>. The status, the clocks and the outstanding work are reconciled with the new number in the same
          transaction; everyone seated on the requisition is told.
        </p>
        <p className="muted">Refused when the new number is below what is already filled or committed, and when you are the person who proposed it.</p>
        <div className="acts">
          <button className="btn" onClick={() => setDlg(null)}>
            Cancel
          </button>
          <button
            className="btn btn--primary"
            disabled={approve.isPending || !p.requisition_id}
            onClick={() => p.requisition_id && void approve.mutateAsync({ requisition_id: p.requisition_id, proposal_id: p.proposal_id }).then(() => setDlg(null))}
          >
            Approve
          </button>
        </div>
      </Dialog>
      <ReasonDialog
        title="Turn the draft down"
        label="Why"
        confirm="Turn it down"
        open={dlg === "deny"}
        onClose={() => setDlg(null)}
        busy={deny.isPending}
        error={deny.error}
        onConfirm={(reason) => void deny.mutateAsync({ proposal_id: p.proposal_id, reason }).then(() => setDlg(null))}
      >
        <p className="muted">Nothing is sent. The draft stays readable with your reason, and the person who raised it is told.</p>
      </ReasonDialog>
    </div>
  );
}

function beforeAfter(v: unknown, side: "before" | "after"): string {
  const o = v as Record<string, unknown> | null;
  const x = o && typeof o === "object" ? o[side] : undefined;
  return x === null || x === undefined ? "—" : String(x);
}

/** A drafted artifact as it would go out: a subject and body where the template made one, otherwise its fields. */
function ArtifactBody({ artifact }: { artifact: Record<string, unknown> }) {
  const subject = artifact["subject"];
  const body = artifact["body"];
  if (typeof body === "string") {
    return (
      <>
        {typeof subject === "string" && (
          <p>
            <b>Subject:</b> {subject}
          </p>
        )}
        <pre className="itinerary">{body}</pre>
      </>
    );
  }
  const rows = Object.entries(artifact).map(([k, v]) => [k.replace(/_/g, " "), v === null || v === undefined ? "—" : String(v)] as [string, string]);
  return rows.length ? <KV items={rows} /> : <p className="muted">The draft carries nothing to show.</p>;
}
